import React, { useState, useEffect } from 'react';
import { 
  Shield, Eye, CheckCircle, XCircle, Clock, AlertTriangle,
  Star, TrendingUp, Flame, Award, ExternalLink, Calendar,
  Users, FileText, Globe, Microscope, Search, Filter,ShoppingCart, 
  ChevronDown, ChevronUp, Download, BookOpen, Tag, Settings, Package, MapPin, Crown

} from 'lucide-react';
import { DispensaryManagement } from './DispensaryManagement';
import API_URL from '../config/ApiUrl';
import { ArticleDetailModal } from './ArticleDetailModal';
import { DispensaryReviewCard } from './DispendaryReviewCard';
import { ArticleReviewCard } from './ArticleReviewCard';
import { DispensaryDetailModal } from './DispendaryDetailsModel';
import { SystemManagement } from './SystemManagement';
import { ProductManagement } from './ProductReviewPage';
import { CugsAnalyticsDashboard } from './CugsPage';
import { DispensaryProductManagement } from './DispensaryProductManagement';
export const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('research');

  // Admin state
  const [pendingArticles, setPendingArticles] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');


  const [pendingDispensaries, setPendingDispensaries] = useState([]);
  const [dispensaryStats, setDispensaryStats] = useState({});
  const [selectedDispensary, setSelectedDispensary] = useState(null);



  // Check authentication on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        setIsAuthenticated(true);
        loadAdminData();
      }
    } catch (error) {
      console.log('Not authenticated');
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(loginForm),
      });

      const data = await response.json();

      if (response.ok) {
        setIsAuthenticated(true);
        loadAdminData();
      } else {
        setLoginError(data.error || 'Login failed');
      }
    } catch (error) {
      setLoginError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  const loadAdminData = async () => {
    try {
    const [articlesRes, statsRes, dispensariesRes, dispStatsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/pending-articles`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/pending-dispensaries`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/dispensary-stats`, { credentials: 'include' })
      ]);

      if (articlesRes.ok && statsRes.ok) {
        const articlesData = await articlesRes.json();
        const statsData = await statsRes.json();
        const dispensariesData = await dispensariesRes.json();
        const dispStatsData = await dispStatsRes.json();

        setPendingArticles(articlesData.articles);
        setStats(statsData.stats);
        setPendingDispensaries(dispensariesData.dispensaries);
        setDispensaryStats(dispStatsData.stats);
      }
    } catch (error) {
      console.error('Failed to load admin data:', error);
    }
  };

  const handleApprove = async (articleId, options = {}) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/articles/${articleId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(options)
      });

      if (response.ok) {
        setPendingArticles(prev => prev.filter(article => article.id !== articleId));
        setSelectedArticle(null);
        await loadAdminData(); // Refresh stats
      }
    } catch (error) {
      console.error('Failed to approve article:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (articleId, reason = '') => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/articles/${articleId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        setPendingArticles(prev => prev.filter(article => article.id !== articleId));
        setSelectedArticle(null);
        await loadAdminData(); // Refresh stats
      }
    } catch (error) {
      console.error('Failed to reject article:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveDispensary = async (dispensaryId, approvalData = {}) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/dispensaries/${dispensaryId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(approvalData)
      });

      if (!response.ok) {
        // Parse error response
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || 
                            errorData.message || 
                            `Server error: ${response.status}`;
        throw new Error(errorMessage);
      }

      // Success - remove from pending list
      setPendingDispensaries(prev => prev.filter(disp => disp.id !== dispensaryId));
      setSelectedDispensary(null);
      await loadAdminData();
      
    } catch (error) {
      console.error('Failed to approve dispensary:', error);
      // The error will be caught and displayed by the modal's handleApprove function
      throw error; // Re-throw so the modal can catch it
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectDispensary = async (dispensaryId, reason = '') => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/dispensaries/${dispensaryId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        setPendingDispensaries(prev => prev.filter(disp => disp.id !== dispensaryId));
        await loadAdminData();
      }
    } catch (error) {
      console.error('Failed to reject dispensary:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Filter articles based on search and status
  const filteredArticles = pendingArticles.filter(article => {
    const matchesSearch = !searchTerm || 
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.institution.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || article.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Login Form Component
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
         <style>{`
      div::-webkit-scrollbar {
        display: none;
      }
      @media (max-width: 480px) {
        .tab-label {
          display: none !important;
        }
      }
    `}</style>
        <div style={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
          borderRadius: '24px',
          padding: '48px',
          border: '1px solid #333',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          width: '400px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 12px 40px rgba(239, 68, 68, 0.4)'
          }}>
            <Shield size={40} style={{ color: 'white' }} />
          </div>

          <h1 style={{
            margin: '0 0 8px 0',
            fontSize: '28px',
            fontWeight: '800',
            color: '#e5e5e5'
          }}>
            Admin Access
          </h1>
          
          <p style={{
            margin: '0 0 32px 0',
            color: '#888',
            fontSize: '16px'
          }}>
            Cannabis Research Hub - Administrative Panel
          </p>

          <form onSubmit={handleAdminLogin}>
            <div style={{ marginBottom: '20px', textAlign: 'left' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Admin Username
              </label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Enter admin username"
                style={{
                  width: '100%',
                  padding: '16px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '12px',
                  color: '#e5e5e5',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ef4444'}
                onBlur={(e) => e.target.style.borderColor = '#333'}
              />
            </div>

            <div style={{ marginBottom: '24px', textAlign: 'left' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Password
              </label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter admin password"
                style={{
                  width: '100%',
                  padding: '16px',
                  background: '#2a2a2a',
                  border: '2px solid #333',
                  borderRadius: '12px',
                  color: '#e5e5e5',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ef4444'}
                onBlur={(e) => e.target.style.borderColor = '#333'}
              />
            </div>

            {loginError && (
              <div style={{
                padding: '12px',
                background: '#ef444420',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !loginForm.username || !loginForm.password}
              style={{
                width: '100%',
                padding: '16px',
                background: loading ? '#555' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {loading ? 'Authenticating...' : 'Access Admin Panel'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main Admin Dashboard
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0a0a',
      color: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        background: '#1a1a1a',
        borderBottom: '1px solid #333',
        padding: '20px 24px'
        }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            {/* Existing header content */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            {/* Your existing header content here */}
            </div>

            {/* Navigation Tabs */}
           <div style={{
              background: '#0f0f0f',
              padding: '6px',
              borderRadius: '12px',
              border: '1px solid #333',
              overflowX: isMobile() ? 'visible' : 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}>
              <div style={{
                display: isMobile() ? 'grid' : 'flex',
                gridTemplateColumns: isMobile() ? 'repeat(3, 1fr)' : 'none',
                gap: isMobile() ? '6px' : '8px',
                minWidth: isMobile() ? 'auto' : 'max-content',
                paddingBottom: '2px'
              }}>
                {[
                  { id: 'research', label: 'Research Articles', icon: Microscope },
                  { id: 'product', label: 'Product', icon: Package },
                  { id: 'dispensary', label: 'Dispensaries', icon: MapPin },
                  { id: 'dispensary-products', label: 'Dispensary Products', icon: ShoppingCart },
                  { id: 'system', label: 'System', icon: Settings },
                  { id: 'cug', label: 'Cugs Page', icon: Crown },
                ].map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: isMobile() ? 'center' : 'flex-start',
                      gap: isMobile() ? '4px' : '8px',
                      padding: isMobile() ? '8px 4px' : '12px 20px',
                      background: activeSection === section.id 
                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
                        : 'transparent',
                      color: activeSection === section.id ? 'white' : '#888',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: isMobile() ? '12px' : '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                      flexDirection: isMobile() ? 'column' : 'row'
                    }}
                  >
                    <section.icon size={isMobile() ? 14 : 16} />
                    <span style={{
                      display: isMobile() ? 'none' : 'inline'
                    }}>
                      {section.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
        </div>
        </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
        {/* Research Articles Section */}
        {activeSection === 'research' && (
            <>
            {/* Stats Dashboard */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
                marginBottom: '32px'
            }}>
                <div style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                borderRadius: '16px',
                padding: '20px',
                color: 'white'
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <Clock size={20} />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Pending Review</span>
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700' }}>{stats.pending_articles || 0}</div>
                </div>

                <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '16px',
                padding: '20px',
                color: 'white'
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <CheckCircle size={20} />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Approved</span>
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700' }}>{stats.approved_articles || 0}</div>
                </div>

                <div style={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '16px',
                padding: '20px',
                color: 'white'
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <XCircle size={20} />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Rejected</span>
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700' }}>{stats.rejected_articles || 0}</div>
                </div>

                <div style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: '16px',
                padding: '20px',
                color: 'white'
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <TrendingUp size={20} />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>This Week</span>
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700' }}>{stats.recent_submissions || 0}</div>
                </div>
            </div>

            {/* Filters */}
            <div style={{
                background: '#1a1a1a',
                borderRadius: '16px',
                padding: '20px',
                border: '1px solid #333',
                marginBottom: '24px'
            }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                    <Search size={20} style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#888'
                    }} />
                    <input
                    type="text"
                    placeholder="Search articles, authors, institutions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        width: '100%',
                        paddingLeft: '48px',
                        paddingRight: '16px',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        border: '2px solid #333',
                        borderRadius: '12px',
                        fontSize: '14px',
                        background: '#2a2a2a',
                        color: '#e5e5e5',
                        outline: 'none',
                        boxSizing: 'border-box'
                    }}
                    />
                </div>

                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    style={{
                    padding: '12px 16px',
                    border: '2px solid #333',
                    borderRadius: '12px',
                    fontSize: '14px',
                    background: '#2a2a2a',
                    color: '#e5e5e5',
                    cursor: 'pointer'
                    }}
                >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                </select>
                </div>
            </div>

            {/* Articles List */}
            <div style={{ display: 'grid', gap: '20px' }}>
                {filteredArticles.map(article => (
                <ArticleReviewCard
                    key={article.id}
                    article={article}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onViewDetails={setSelectedArticle}
                    actionLoading={actionLoading}
                />
                ))}
                
                {filteredArticles.length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: '64px 24px',
                    background: '#1a1a1a',
                    borderRadius: '20px',
                    border: '1px solid #333'
                }}>
                    <FileText size={48} style={{ color: '#666', marginBottom: '16px' }} />
                    <h3 style={{ margin: '0 0 8px 0', color: '#e5e5e5', fontSize: '20px', fontWeight: '600' }}>
                    No articles found
                    </h3>
                    <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>
                    {searchTerm || filterStatus !== 'all' 
                        ? 'Try adjusting your search or filter criteria'
                        : 'All articles have been reviewed'}
                    </p>
                </div>
                )}
            </div>
            </>
        )}

        {activeSection === 'dispensary-products' && (
          <DispensaryProductManagement />
        )}


        {/* Product Section */}
        {activeSection === 'product' && (
            // <div style={{
            // background: '#1a1a1a',
            // borderRadius: '20px',
            // padding: '40px',
            // border: '1px solid #333',
            // textAlign: 'center'
            // }}>
            // <Package size={48} style={{ color: '#666', marginBottom: '16px' }} />
            // <h3 style={{ margin: '0 0 8px 0', color: '#e5e5e5', fontSize: '20px', fontWeight: '600' }}>
            //     Product Management
            // </h3>
            // <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>
            //     Product tools coming soon
            // </p>
            // </div>
            <ProductManagement />
        )}

        </div>
        {activeSection === 'cug' && (
          <CugsAnalyticsDashboard />
        )}
        {activeSection === 'dispensary' && (
          <DispensaryManagement
            API_URL={API_URL}
            pendingDispensaries={pendingDispensaries}
            setPendingDispensaries={setPendingDispensaries}
            dispensaryStats={dispensaryStats}
            setDispensaryStats={setDispensaryStats}
            selectedDispensary={selectedDispensary}
            setSelectedDispensary={setSelectedDispensary}
            actionLoading={actionLoading}
            setActionLoading={setActionLoading}
            handleApproveDispensary={handleApproveDispensary}
            handleRejectDispensary={handleRejectDispensary}
            loadAdminData={loadAdminData}
          />
          // <>
          //   {/* Dispensary Stats */}
          //   <div style={{
          //     display: 'grid',
          //     gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          //     gap: '20px',
          //     marginBottom: '32px'
          //   }}>
          //     <div style={{
          //       background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
          //       borderRadius: '16px',
          //       padding: '20px',
          //       color: 'white'
          //     }}>
          //       <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          //         <Clock size={20} />
          //         <span style={{ fontSize: '14px', fontWeight: '500' }}>Pending Review</span>
          //       </div>
          //       <div style={{ fontSize: '28px', fontWeight: '700' }}>{dispensaryStats.pending_dispensaries || 0}</div>
          //     </div>

          //     <div style={{
          //       background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          //       borderRadius: '16px',
          //       padding: '20px',
          //       color: 'white'
          //     }}>
          //       <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          //         <CheckCircle size={20} />
          //         <span style={{ fontSize: '14px', fontWeight: '500' }}>Total Active</span>
          //       </div>
          //       <div style={{ fontSize: '28px', fontWeight: '700' }}>{dispensaryStats.total_dispensaries || 0}</div>
          //     </div>

          //     <div style={{
          //       background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          //       borderRadius: '16px',
          //       padding: '20px',
          //       color: 'white'
          //     }}>
          //       <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          //         <TrendingUp size={20} />
          //         <span style={{ fontSize: '14px', fontWeight: '500' }}>This Week</span>
          //       </div>
          //       <div style={{ fontSize: '28px', fontWeight: '700' }}>{dispensaryStats.recent_submissions || 0}</div>
          //     </div>
          //   </div>

          //   {/* Pending Dispensaries */}
          //   <div style={{ display: 'grid', gap: '20px' }}>
          //     {pendingDispensaries.map(dispensary => (
          //       <DispensaryReviewCard
          //         key={dispensary.id}
          //         dispensary={dispensary}
          //         onApprove={handleApproveDispensary}
          //         onReject={handleRejectDispensary}
          //         onViewDetails={setSelectedDispensary}
          //         actionLoading={actionLoading}
          //       />
          //     ))}
              
          //     {pendingDispensaries.length === 0 && (
          //       <div style={{
          //         textAlign: 'center',
          //         padding: '64px 24px',
          //         background: '#1a1a1a',
          //         borderRadius: '20px',
          //         border: '1px solid #333'
          //       }}>
          //         <MapPin size={48} style={{ color: '#666', marginBottom: '16px' }} />
          //         <h3 style={{ margin: '0 0 8px 0', color: '#e5e5e5', fontSize: '20px', fontWeight: '600' }}>
          //           No pending dispensaries
          //         </h3>
          //         <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>
          //           All dispensary submissions have been reviewed
          //         </p>
          //       </div>
          //     )}
          //   </div>
          // </>
        )}

        {/* Article Detail Modal - Place this outside the main container */}
        {selectedArticle && (
        <ArticleDetailModal
            article={selectedArticle}
            onClose={() => setSelectedArticle(null)}
            onApprove={handleApprove}
            onReject={handleReject}
            actionLoading={actionLoading}
        />
        )}
        {selectedDispensary && (
          <DispensaryDetailModal
            dispensary={selectedDispensary}
            onClose={() => setSelectedDispensary(null)}
            onApprove={handleApproveDispensary}
            onReject={handleRejectDispensary}
            actionLoading={actionLoading}
          />
        )}

        {activeSection === 'system' && (
            <SystemManagement />
            )}
      <style jsx>{`
        div::-webkit-scrollbar {
          display: none;
        }
        @media (max-width: 768px) {
          .tab-label {
            display: none !important;
          }
        }
      `}</style>  
    </div>
  );
};
